/**
 * Copyright © 2025 Farmlands Co-operative Society Limited. All rights reserved.
 *
 * This document contains confidential information and is the property of Farmlands.
 * Unauthorised reproduction or distribution of any part of this publication is prohibited without written consent.
 */

import { createAsyncThunk } from "@reduxjs/toolkit";
import { AxiosError } from "axios";

import apiClient from "../../../../services/api-client";
import { RootState } from "../../../../store/rootReducer";
import { productToCheckoutItem } from "../../utils/productToCheckoutItem";
import { SubmitCartReducerAction } from "../cart.slice";
import {
  CartAPIError,
  CartDeliveryMode,
  CartObject,
  CheckoutPayloadV3,
  ErrorCode,
  SubmitCartApiResponse,
} from "../cart.types";

export const checkoutV2 = createAsyncThunk<
  SubmitCartReducerAction,
  { cart: CartObject; accountNumber: string },
  { rejectValue: CartAPIError; getState: RootState }
>("cart/orders", async ({ cart }, { rejectWithValue }) => {
  try {
    if (
      cart.items.length > 0 &&
      cart.cartDeliveryMode === CartDeliveryMode.PICKUP &&
      cart.pickupStore?.inventoryLocationId
    ) {
      const payload: CheckoutPayloadV3 = createPickupPayload(cart);

      const request = await apiClient.post<SubmitCartApiResponse, CheckoutPayloadV3>(
        `/carts/${cart.onlineCartId}/checkout`,
        payload
      );
      return {
        offlineCartId: cart.offlineCartId,
        store: cart.pickupStore,
        data: request.data,
      };
    } else if (cart.items.length > 0 && cart.cartDeliveryMode === CartDeliveryMode.DELIVERY) {
      const payload: CheckoutPayloadV3 = createDeliveryPayload(cart);

      const request = await apiClient.post<SubmitCartApiResponse, CheckoutPayloadV3>(
        `/carts/${cart.onlineCartId}/checkout`,
        payload
      );
      return {
        offlineCartId: cart.offlineCartId,
        store: cart.pickupStore,
        data: request.data,
      };
    } else {
      return rejectWithValue({
        type: ErrorCode.InvalidCart,
      });
    }
  } catch (err) {
    const error: AxiosError = err as AxiosError;
    if (!error.response) {
      if (error.code === AxiosError.ERR_NETWORK) {
        return rejectWithValue({ type: ErrorCode.OfflineError } as CartAPIError);
      }
      throw err;
    }

    return rejectWithValue(error.response.data as CartAPIError);
  }
});

const createPickupPayload = (cart: CartObject): CheckoutPayloadV3 => {
  return {
    customerName: cart.customerName,
    customerReference: cart.customerReference ?? "",
    dairySupplyNumber: cart.deliveryDetails?.dairySupplyNumber,
    deliveryMode: cart.cartDeliveryMode,
    email: cart.email ?? "",
    items: cart.items.map((item) => productToCheckoutItem(item)),
    notes: cart.notes ?? "",
    internalNotes: cart.internalNotes ?? "",
    phone: cart.phone ?? "",
    storeLocationId: cart.pickupStore?.inventoryLocationId,
  };
};

const createDeliveryPayload = (cart: CartObject): CheckoutPayloadV3 => {
  return {
    customerName: cart.customerName,
    customerReference: cart.customerReference ?? "",
    dairySupplyNumber: cart.deliveryDetails?.dairySupplyNumber,
    deliveryInstructions: cart.deliveryDetails?.deliveryInstructions,
    deliveryAddress: {
      city: cart.deliveryDetails?.city,
      name: cart.deliveryDetails?.addressName,
      postcode: cart.deliveryDetails?.postCode,
      streetAddress: cart.deliveryDetails?.streetAddress,
      recordId: cart.deliveryDetails?.recordId,
    },
    deliveryMode: cart.cartDeliveryMode,
    email: cart.email ?? "",
    isUrgent: cart.deliveryDetails?.urgent,
    items: cart.items.map((item) => productToCheckoutItem(item)),
    notes: cart.notes ?? "",
    internalNotes: cart.internalNotes ?? "",
    phone: cart.phone ?? "",
  };
};
